import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {useState, useEffect} from "react";
import {RTL} from "../../cache/CacheRtl";
import {savePartnership, updatePartnership} from "../../services/PartnershipService";
import {uploadImage} from "../../services/MediaUploadService";
import {useMessageContext} from "../../contexts/MessageContext";
import {t} from "../dashboard/coach/coachTokens";

// Mirrors the PartnershipCategory enum on the server.
export const CATEGORY_OPTIONS = [
  {value: "NUTRITION", label: "תזונה ותוספים"},
  {value: "EQUIPMENT", label: "ציוד"},
  {value: "SOFTWARE", label: "תוכנה"},
  {value: "HEALTH", label: "בריאות"},
  {value: "EDUCATION", label: "הכשרה מקצועית"},
  {value: "WELLNESS", label: "וולנס"},
  {value: "OTHER", label: "אחר"},
];

// Mirrors the PartnershipBadge enum on the server (null = no badge).
export const BADGE_OPTIONS = [
  {value: "POPULAR", label: "פופולרי"},
  {value: "NEW", label: "חדש"},
  {value: "EXCLUSIVE", label: "בלעדי"},
];

// Badge label + colors (matches the HTML mock: popular=teal, new=dark, exclusive=purple).
export const BADGE_META = {
  POPULAR: {label: "פופולרי", bg: "#01BBA7", color: "#fff"},
  NEW: {label: "חדש", bg: "#111827", color: "#fff"},
  EXCLUSIVE: {label: "בלעדי", bg: "#7C3AED", color: "#fff"},
};

const EMPTY_FORM = {
  name: "",
  category: "",
  badge: "",
  tagline: "",
  description: "",
  offer: "",
  terms: "",
  websiteUrl: "",
  dealUrl: "",
  logoUrl: "",
  backgroundColor: "",
  contactEmail: "",
};

// Matches the server @Pattern: #RGB, #RRGGBB, or #RRGGBBAA.
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const PartnershipDialog = ({isOpen, close, setReload, editPartnership = null}) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const {notificationHandler} = useMessageContext();
  const isEdit = editPartnership !== null;

  useEffect(() => {
    if (isOpen) {
      if (isEdit) {
        setFormData({
          name: editPartnership.name || "",
          category: editPartnership.category || "",
          badge: editPartnership.badge || "",
          tagline: editPartnership.tagline || "",
          description: editPartnership.description || "",
          offer: editPartnership.offer || "",
          terms: editPartnership.terms || "",
          websiteUrl: editPartnership.websiteUrl || "",
          dealUrl: editPartnership.dealUrl || "",
          logoUrl: editPartnership.logoUrl || "",
          backgroundColor: editPartnership.backgroundColor || "",
          contactEmail: editPartnership.contactEmail || "",
        });
      } else {
        setFormData(EMPTY_FORM);
      }
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editPartnership]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({...prev, [field]: value}));
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: ""}));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "יש להזין שם שותף";
    }
    if (!formData.category) {
      newErrors.category = "יש לבחור קטגוריה";
    }
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = "יש להזין אימייל ליצירת קשר";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail.trim())) {
      newErrors.contactEmail = "כתובת אימייל לא תקינה";
    }
    if (formData.backgroundColor.trim() && !HEX_COLOR_RE.test(formData.backgroundColor.trim())) {
      newErrors.backgroundColor = "צבע לא תקין (למשל 16243B#)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const cdnUrl = await uploadImage(file);
      handleInputChange("logoUrl", cdnUrl);
    } catch (e) {
      console.error("Error uploading logo:", e);
      notificationHandler.error("העלאת הלוגו נכשלה");
    } finally {
      setUploading(false);
      // Allow re-selecting the same file.
      event.target.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        category: formData.category,
        badge: formData.badge || null,
        tagline: formData.tagline || null,
        description: formData.description || null,
        offer: formData.offer || null,
        terms: formData.terms || null,
        websiteUrl: formData.websiteUrl || null,
        dealUrl: formData.dealUrl || null,
        logoUrl: formData.logoUrl || null,
        backgroundColor: formData.backgroundColor.trim() || null,
        contactEmail: formData.contactEmail.trim(),
      };

      if (isEdit) {
        await updatePartnership(editPartnership.id, payload);
        notificationHandler.success("השותף עודכן בהצלחה");
      } else {
        await savePartnership(payload);
        notificationHandler.success("השותף נוצר בהצלחה");
      }

      setReload();
      close();
    } catch (error) {
      console.error("Error saving partnership:", error);
      const msg = error.response?.data?.message || "שמירת השותף נכשלה";
      notificationHandler.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={close} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle>
        <Typography variant="h6" component="div">
          {isEdit ? "עריכת שותף" : "הוספת שותף חדש"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{display: "flex", flexDirection: "column", gap: 3, pt: 2}}>
          <RTL>
            {/* Logo */}
            <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
              <Avatar
                src={formData.logoUrl || undefined}
                variant="rounded"
                sx={{width: 56, height: 56, bgcolor: t.bg, color: t.t2}}
              >
                {formData.name ? formData.name.charAt(0) : "?"}
              </Avatar>
              <Button variant="outlined" component="label" disabled={uploading}>
                {uploading ? "מעלה..." : "העלאת לוגו"}
                <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
              </Button>
              {uploading && <CircularProgress size={20} />}
            </Box>

            <TextField
              label="שם השותף *"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              disabled={loading}
            />

            <FormControl fullWidth disabled={loading} error={!!errors.category}>
              <InputLabel>קטגוריה *</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => handleInputChange("category", e.target.value)}
                label="קטגוריה *"
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && (
                <Typography color="error" variant="caption" sx={{mt: 0.5, mr: 1.5}}>
                  {errors.category}
                </Typography>
              )}
            </FormControl>

            {/* Card background color — native swatch + hex field. Empty = per-category default. */}
            <Box>
              <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                <Box
                  component="input"
                  type="color"
                  disabled={loading}
                  aria-label="בחירת צבע רקע"
                  value={
                    HEX_COLOR_RE.test(formData.backgroundColor)
                      ? formData.backgroundColor
                      : "#1A1A2E"
                  }
                  onChange={(e) => handleInputChange("backgroundColor", e.target.value)}
                  sx={{
                    width: 48,
                    height: 48,
                    padding: 0,
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    background: "none",
                    cursor: loading ? "default" : "pointer",
                    flexShrink: 0,
                  }}
                />
                <TextField
                  label="צבע רקע לכרטיס"
                  value={formData.backgroundColor}
                  onChange={(e) => handleInputChange("backgroundColor", e.target.value)}
                  error={!!errors.backgroundColor}
                  helperText={errors.backgroundColor || "ריק = צבע ברירת המחדל לפי הקטגוריה"}
                  fullWidth
                  disabled={loading}
                  placeholder="#16243B"
                />
                {formData.backgroundColor.trim() && (
                  <Button
                    onClick={() => handleInputChange("backgroundColor", "")}
                    disabled={loading}
                    size="small"
                    sx={{flexShrink: 0, minWidth: "auto"}}
                  >
                    איפוס
                  </Button>
                )}
              </Box>
            </Box>

            <FormControl fullWidth disabled={loading}>
              <InputLabel>תגית</InputLabel>
              <Select
                value={formData.badge}
                onChange={(e) => handleInputChange("badge", e.target.value)}
                label="תגית"
              >
                <MenuItem value="">
                  <em>ללא</em>
                </MenuItem>
                {BADGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="כותרת משנה"
              value={formData.tagline}
              onChange={(e) => handleInputChange("tagline", e.target.value)}
              fullWidth
              disabled={loading}
            />

            <TextField
              label="תיאור"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
              multiline
              minRows={3}
              disabled={loading}
            />

            <TextField
              label="הטבה (למשל 20% הנחה)"
              value={formData.offer}
              onChange={(e) => handleInputChange("offer", e.target.value)}
              fullWidth
              disabled={loading}
            />

            <TextField
              label="תנאי המבצע"
              value={formData.terms}
              onChange={(e) => handleInputChange("terms", e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={loading}
            />

            <TextField
              label="אתר השותף"
              value={formData.websiteUrl}
              onChange={(e) => handleInputChange("websiteUrl", e.target.value)}
              fullWidth
              disabled={loading}
              placeholder="https://..."
            />

            <TextField
              label="קישור למבצע"
              value={formData.dealUrl}
              onChange={(e) => handleInputChange("dealUrl", e.target.value)}
              fullWidth
              disabled={loading}
              placeholder="https://..."
            />

            <TextField
              label="אימייל ליצירת קשר *"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleInputChange("contactEmail", e.target.value)}
              error={!!errors.contactEmail}
              helperText={errors.contactEmail || "פניות מתעניינים יישלחו לכתובת זו"}
              fullWidth
              disabled={loading}
            />
          </RTL>
        </Box>
      </DialogContent>

      <DialogActions sx={{p: 3, pt: 1}}>
        <Button onClick={close} disabled={loading} variant="outlined">
          ביטול
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || uploading}
          variant="contained"
          sx={{minWidth: 120}}
        >
          {loading ? "שומר..." : isEdit ? "עדכן" : "צור שותף"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnershipDialog;
